import { useState, useCallback, useEffect, useRef } from 'react';
import {
  AfternoonTransferSession,
  BreadTransferConfig,
  UpdateAfternoonTransferRowPayload,
  AfternoonTransferRow
} from '../types/bread-transfers.types';
import {
  mockBreadTransferConfig
} from '../mocks/bread-transfers.mock';
import {
  fetchSession,
  createSessionApi,
  updateRowApi,
  completeSessionApi,
  resetSessionApi
} from '../services/breadTransfers.service';
import type { ApiSession, ApiRow } from '../services/breadTransfers.service';

const debounceTimers: Record<string, ReturnType<typeof setTimeout>> = {};

// Parsea "MAC 01 (ANGAMOS)" → { code: "MAC 01", name: "ANGAMOS" }
const parseLocationName = (nombre: string): { code: string; name: string } => {
  const match = nombre?.match(/^(MAC \d+)\s*\((.+)\)$/);
  if (match) return { code: match[1], name: match[2] };
  return { code: nombre || '', name: nombre || '' };
};

const mapApiToAfternoonSession = (api: ApiSession): AfternoonTransferSession => {
  const locations: AfternoonTransferRow[] = (api.rows || []).map((row: ApiRow) => {
    const parsed = parseLocationName(row.location?.nombre || '');
    return {
      id: row.id,
      locationId: String(row.locationId),
      locationCode: parsed.code,
      locationName: parsed.name,
      historicAvgSaleTotal: row.historicAvgSaleTotal || 0,
      totalSalePreviousDay: row.totalSalePreviousDay || 0,
      totalSalePreviousWeek: row.totalSalePreviousWeek || 0,
      quantityToRequest: row.quantityToRequest || 0,
      safetyFactor: api.safetyFactor || 0,
      requestToBakery: row.requestToBakery || 0,
      afternoonSaleHistoric: row.afternoonSaleHistoric || 0,
      afternoonSalePreviousDay: row.afternoonSalePreviousDay || 0,
      previousWeekAfternoonSale: row.previousWeekAfternoonSale || 0,
      morningTodaySale: row.morningTodaySale || 0,
      todayAfternoonSale: row.todayAfternoonSale || 0,
      saleUntilNow: row.saleUntilNow || 0,
      currentStock: row.currentStock || 0,
      suggested: row.suggested || 0
    };
  });

  const totals = locations.reduce((acc, loc) => ({
    historicAvgSaleTotal: acc.historicAvgSaleTotal + loc.historicAvgSaleTotal,
    totalSalePreviousDay: acc.totalSalePreviousDay + loc.totalSalePreviousDay,
    totalSalePreviousWeek: acc.totalSalePreviousWeek + loc.totalSalePreviousWeek,
    quantityToRequest: acc.quantityToRequest + loc.quantityToRequest,
    safetyFactor: acc.safetyFactor + loc.safetyFactor,
    requestToBakery: acc.requestToBakery + loc.requestToBakery,
    afternoonSaleHistoric: acc.afternoonSaleHistoric + loc.afternoonSaleHistoric,
    afternoonSalePreviousDay: acc.afternoonSalePreviousDay + loc.afternoonSalePreviousDay,
    previousWeekAfternoonSale: acc.previousWeekAfternoonSale + loc.previousWeekAfternoonSale,
    morningTodaySale: acc.morningTodaySale + loc.morningTodaySale,
    todayAfternoonSale: acc.todayAfternoonSale + loc.todayAfternoonSale,
    saleUntilNow: acc.saleUntilNow + loc.saleUntilNow,
    currentStock: acc.currentStock + loc.currentStock,
    suggested: acc.suggested + loc.suggested
  }), {
    historicAvgSaleTotal: 0, totalSalePreviousDay: 0, totalSalePreviousWeek: 0,
    quantityToRequest: 0, safetyFactor: 0, requestToBakery: 0,
    afternoonSaleHistoric: 0, afternoonSalePreviousDay: 0, previousWeekAfternoonSale: 0,
    morningTodaySale: 0, todayAfternoonSale: 0, saleUntilNow: 0,
    currentStock: 0, suggested: 0
  });

  return {
    id: String(api.id),
    date: api.date,
    shift: 'afternoon-transfer',
    locations,
    totals,
    status: api.status,
    createdAt: api.createdAt,
    updatedAt: api.updatedAt
  };
};

export const useAfternoonTransfers = (selectedDate: string = new Date().toISOString().split('T')[0]) => {
  const [config] = useState<BreadTransferConfig>(mockBreadTransferConfig);
  const [sessions, setSessions] = useState<AfternoonTransferSession[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const loadedRef = useRef<string>('');

  // Cargar sesión desde API al cambiar la fecha
  useEffect(() => {
    const key = selectedDate;
    if (loadedRef.current === key) return;
    loadedRef.current = key;

    const loadSession = async () => {
      setLoading(true);
      setError(null);
      try {
        const apiSession = await fetchSession(selectedDate, 'afternoon-transfer');
        if (apiSession) {
          const session = mapApiToAfternoonSession(apiSession);
          setSessions(prev => {
            const otherDates = prev.filter(s => s.date !== selectedDate);
            return [...otherDates, session];
          });
        }
      } catch (err) {
        console.error('Error loading afternoon transfer session:', err);
        setError(err instanceof Error ? err.message : 'Error cargando sesión');
      } finally {
        setLoading(false);
      }
    };

    loadSession();
  }, [selectedDate]);

  const calculateSuggested = useCallback((row: Omit<AfternoonTransferRow, 'suggested'>): number => {
    const avgHistoric = (row.afternoonSaleHistoric + row.afternoonSalePreviousDay + row.previousWeekAfternoonSale) / 3;
    const currentNeed = Math.max(0, avgHistoric - row.currentStock);
    return Math.round(currentNeed);
  }, []);

  const calculateTotals = useCallback((locations: AfternoonTransferRow[]) => {
    return locations.reduce((totals, location) => ({
      historicAvgSaleTotal: totals.historicAvgSaleTotal + location.historicAvgSaleTotal,
      totalSalePreviousDay: totals.totalSalePreviousDay + location.totalSalePreviousDay,
      totalSalePreviousWeek: totals.totalSalePreviousWeek + location.totalSalePreviousWeek,
      quantityToRequest: totals.quantityToRequest + location.quantityToRequest,
      safetyFactor: totals.safetyFactor + location.safetyFactor,
      requestToBakery: totals.requestToBakery + location.requestToBakery,
      afternoonSaleHistoric: totals.afternoonSaleHistoric + location.afternoonSaleHistoric,
      afternoonSalePreviousDay: totals.afternoonSalePreviousDay + location.afternoonSalePreviousDay,
      previousWeekAfternoonSale: totals.previousWeekAfternoonSale + location.previousWeekAfternoonSale,
      morningTodaySale: totals.morningTodaySale + location.morningTodaySale,
      todayAfternoonSale: totals.todayAfternoonSale + location.todayAfternoonSale,
      saleUntilNow: totals.saleUntilNow + location.saleUntilNow,
      currentStock: totals.currentStock + location.currentStock,
      suggested: totals.suggested + location.suggested
    }), {
      historicAvgSaleTotal: 0, totalSalePreviousDay: 0, totalSalePreviousWeek: 0,
      quantityToRequest: 0, safetyFactor: 0, requestToBakery: 0,
      afternoonSaleHistoric: 0, afternoonSalePreviousDay: 0, previousWeekAfternoonSale: 0,
      morningTodaySale: 0, todayAfternoonSale: 0, saleUntilNow: 0,
      currentStock: 0, suggested: 0
    });
  }, []);

  const getSession = useCallback((): AfternoonTransferSession | null => {
    return sessions.find(session => session.date === selectedDate) || null;
  }, [sessions, selectedDate]);

  const createSession = useCallback(async (): Promise<AfternoonTransferSession> => {
    setLoading(true);
    setError(null);
    try {
      const apiSession = await createSessionApi(selectedDate, 'afternoon-transfer');
      const session = mapApiToAfternoonSession(apiSession);
      setSessions(prev => [...prev.filter(s => s.date !== selectedDate), session]);
      return session;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error creando sesión');
      throw err;
    } finally {
      setLoading(false);
    }
  }, [selectedDate]);

  const updateTransferRow = useCallback(async (
    payload: UpdateAfternoonTransferRowPayload
  ): Promise<void> => {
    // Actualización optimista local
    setSessions(prev => prev.map(session => {
      if (session.date !== selectedDate) {
        return session;
      }

      const updatedLocations = session.locations.map(location => {
        if (location.locationId !== payload.locationId) {
          return location;
        }

        const updatedLocation = {
          ...location,
          [payload.field]: payload.value
        };
        updatedLocation.suggested = calculateSuggested(updatedLocation);
        return updatedLocation;
      });

      return {
        ...session,
        locations: updatedLocations,
        totals: calculateTotals(updatedLocations),
        updatedAt: new Date().toISOString()
      };
    }));

    // Debounce la llamada a la API
    const session = sessions.find(s => s.date === selectedDate);
    if (!session) return;

    const row = session.locations.find(l => l.locationId === payload.locationId);
    if (!row?.id) return;

    const sessionId = Number(session.id);
    const rowId = row.id;
    const timerKey = `afternoon-${rowId}-${payload.field}`;

    if (debounceTimers[timerKey]) {
      clearTimeout(debounceTimers[timerKey]);
    }

    debounceTimers[timerKey] = setTimeout(async () => {
      try {
        await updateRowApi(sessionId, rowId, payload.field, payload.value);

        // También persistir el campo suggested si cambió un campo que lo afecta
        const affectsFields = ['afternoonSaleHistoric', 'afternoonSalePreviousDay', 'previousWeekAfternoonSale', 'currentStock'];
        if (affectsFields.includes(payload.field)) {
          const currentSession = sessions.find(s => s.date === selectedDate);
          const currentRow = currentSession?.locations.find(l => l.locationId === payload.locationId);
          if (currentRow) {
            const updatedRow = { ...currentRow, [payload.field]: payload.value };
            const avgHistoric = (updatedRow.afternoonSaleHistoric + updatedRow.afternoonSalePreviousDay + updatedRow.previousWeekAfternoonSale) / 3;
            const newSuggested = Math.round(Math.max(0, avgHistoric - updatedRow.currentStock));
            await updateRowApi(sessionId, rowId, 'suggested', newSuggested);
          }
        }
      } catch (err) {
        console.error('Error persisting afternoon row update:', err);
        setError(err instanceof Error ? err.message : 'Error guardando cambio');
      }
      delete debounceTimers[timerKey];
    }, 300);
  }, [selectedDate, sessions, calculateSuggested, calculateTotals]);

  const completeSession = useCallback(async (): Promise<void> => {
    const session = sessions.find(s => s.date === selectedDate);
    if (!session) throw new Error('Sesión no encontrada');

    setLoading(true);
    setError(null);
    try {
      await completeSessionApi(Number(session.id));
      setSessions(prev => prev.map(s => {
        if (s.date === selectedDate) {
          return { ...s, status: 'completed' as const, updatedAt: new Date().toISOString() };
        }
        return s;
      }));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error completando sesión');
      throw err;
    } finally {
      setLoading(false);
    }
  }, [selectedDate, sessions]);

  const resetSession = useCallback(async (): Promise<void> => {
    const session = sessions.find(s => s.date === selectedDate);
    if (!session) throw new Error('Sesión no encontrada');

    setLoading(true);
    setError(null);
    try {
      await resetSessionApi(Number(session.id));
      setSessions(prev => prev.map(s => {
        if (s.date === selectedDate) {
          return { ...s, status: 'draft' as const, updatedAt: new Date().toISOString() };
        }
        return s;
      }));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error reiniciando sesión');
      throw err;
    } finally {
      setLoading(false);
    }
  }, [selectedDate, sessions]);

  return {
    config,
    sessions,
    loading,
    error,
    getSession,
    createSession,
    updateTransferRow,
    completeSession,
    resetSession
  };
};
